import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  InstallDesktop as InstallIcon,
  CheckCircle,
  DeleteOutline as DeleteIcon,
  SystemUpdateAlt as UpdateIcon,
  Publish as PublishIcon,
  Add as AddIcon,
  Tune as ManageIcon,
  AutoAwesomeMotion as DemoIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useConnection } from '@/app/contexts/ConnectionContext';
import { flushAppAfterModuleChange } from '@/app/workspace/flushAppAfterModuleChange';
import {
  marketplaceApi,
  designerApi,
  CatalogModule,
  CatalogBundle,
} from '@/utils/api';
import { PublishModuleDialog } from '@/designer/components/PublishModuleDialog';
import { NewModuleDialog } from './NewModuleDialog';

type ToastState = { severity: 'success' | 'error' | 'info' | 'warning'; message: string } | null;

function errorDetail(err: unknown): string {
  const e = err as { message?: string; detail?: string; response?: { data?: { detail?: string } } };
  return (
    e?.response?.data?.detail ||
    e?.detail ||
    e?.message ||
    'Request failed'
  );
}

export const ModulesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedConnection } = useConnection();
  const connectionId = selectedConnection?.id;

  const [confirmUninstall, setConfirmUninstall] = useState<CatalogModule | null>(null);
  const [publishTarget, setPublishTarget] = useState<CatalogModule | null>(null);
  const [publishLocalKey, setPublishLocalKey] = useState<string | null>(null);
  const [authoring, setAuthoring] = useState<{ moduleKey: string | null } | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  const catalogQueryKey = ['marketplaceCatalog', connectionId] as const;
  const localQueryKey = ['localModules', connectionId] as const;

  const { data, isLoading, error } = useQuery({
    queryKey: catalogQueryKey,
    queryFn: () => marketplaceApi.getCatalog(connectionId!),
    enabled: !!connectionId,
  });

  const { data: localData } = useQuery({
    queryKey: localQueryKey,
    queryFn: () => designerApi.getLocalModules(connectionId!),
    enabled: !!connectionId,
  });

  const refetchCatalog = () => {
    void queryClient.invalidateQueries({ queryKey: catalogQueryKey });
  };

  const refetchLocal = () => {
    void queryClient.invalidateQueries({ queryKey: localQueryKey });
  };

  const installMutation = useMutation({
    mutationFn: (moduleKey: string) => marketplaceApi.installModule(moduleKey, connectionId!),
    onSuccess: refetchCatalog,
  });

  const updateMutation = useMutation({
    mutationFn: (moduleKey: string) => marketplaceApi.updateModule(moduleKey, connectionId!),
    onSuccess: refetchCatalog,
  });

  const uninstallMutation = useMutation({
    mutationFn: (moduleKey: string) => marketplaceApi.uninstallModule(moduleKey, connectionId!),
    onSuccess: refetchCatalog,
  });

  const bundleInstallMutation = useMutation({
    mutationFn: (bundleKey: string) => marketplaceApi.installBundle(bundleKey, connectionId!),
    onSuccess: refetchCatalog,
  });

  const handleInstall = async (moduleKey: string) => {
    try {
      await installMutation.mutateAsync(moduleKey);
      setToast({ severity: 'success', message: 'Module installed.' });
    } catch (err: unknown) {
      setToast({ severity: 'error', message: errorDetail(err) });
    }
  };

  const handleUpdate = async (moduleKey: string) => {
    try {
      await updateMutation.mutateAsync(moduleKey);
      setToast({ severity: 'success', message: 'Module updated.' });
    } catch (err: unknown) {
      setToast({ severity: 'error', message: errorDetail(err) });
    }
  };

  const demoMutation = useMutation({
    mutationFn: (moduleKey: string) => marketplaceApi.loadDemoData(moduleKey, connectionId!),
  });

  const handleLoadDemo = async (mod: CatalogModule) => {
    try {
      const r = await demoMutation.mutateAsync(mod.module_key);
      if (r.errors?.length) {
        setToast({ severity: 'error', message: `"${mod.display_name}": ${r.errors[0]}` });
        return;
      }
      const total = Object.values(r.loaded ?? {}).reduce((sum, n) => sum + n, 0);
      setToast({ severity: 'success', message: `Demo data loaded: ${total} records.` });
    } catch (err: unknown) {
      setToast({ severity: 'error', message: errorDetail(err) });
    }
  };

  const handleUninstall = async (mod: CatalogModule) => {
    setConfirmUninstall(null);
    try {
      const result = await uninstallMutation.mutateAsync(mod.module_key);
      const next = connectionId
        ? flushAppAfterModuleChange(connectionId, {
            removedObjectNames: result?.removed_objects,
            currentPath: window.location.pathname,
            queryClient,
          })
        : null;
      if (next) navigate(next);
      setToast({
        severity: 'success',
        message: `Uninstalled "${mod.display_name}".`,
      });
    } catch (err: unknown) {
      setToast({ severity: 'error', message: errorDetail(err) });
    }
  };

  const handleInstallBundle = async (bundle: CatalogBundle) => {
    try {
      const r = await bundleInstallMutation.mutateAsync(bundle.bundle_key);
      if (r.errors?.length) {
        setToast({ severity: 'error', message: `Bundle "${bundle.display_name}": ${r.errors[0]}` });
      } else {
        setToast({
          severity: 'success',
          message: `Bundle "${bundle.display_name}" installed: ${r.installed.join(', ') || 'none'}`,
        });
      }
    } catch (err: unknown) {
      setToast({ severity: 'error', message: errorDetail(err) });
    }
  };

  if (!connectionId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">Select a connection first to manage modules.</Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load modules. Please refresh.</Alert>
      </Container>
    );
  }

  const modules = data?.modules ?? [];
  const bundles = data?.bundles ?? [];
  const registryUnreachable = data?.registry_unreachable ?? false;
  const localModules = localData?.modules ?? [];
  const unassignedObjects = localData?.unassigned ?? [];
  const catalogKeys = new Set(modules.map((m) => m.module_key));
  const localOnly = localModules.filter((m) => !catalogKeys.has(m.module_key));
  const compositionOf = (key: string) =>
    localModules.find((m) => m.module_key === key)?.object_names ?? [];
  const foreignOwnersFor = (key: string | null): Record<string, string> => {
    const owners: Record<string, string> = {};
    for (const m of localModules) {
      if (m.module_key === key) continue;
      for (const name of m.object_names) owners[name] = m.module_key;
    }
    return owners;
  };
  const handleLocalPublished = (
    moduleKey: string,
    version: string,
    meta?: { stamped: boolean },
  ) => {
    setToast({
      severity: meta?.stamped === false ? 'warning' : 'success',
      message:
        meta?.stamped === false
          ? 'Published, but this connection still shows the previous version'
          : `Published "${moduleKey}" v${version}.`,
    });
    refetchLocal();
    refetchCatalog();
  };
  const isInstallBusy = installMutation.isLoading;
  const isUpdateBusy = updateMutation.isLoading;
  const isUninstallBusy = uninstallMutation.isLoading;
  const isBundleBusy = bundleInstallMutation.isLoading;

  const renderModuleCard = (mod: CatalogModule) => {
    const versionLabel =
      mod.status === 'installed' || mod.status === 'update_available'
        ? `v${mod.installed_version ?? mod.version}`
        : `v${mod.version}`;
    const requires = (mod.dependencies ?? []).map((key) => {
      const owner = modules.find((other) => other.module_key === key);
      return owner?.display_name || key;
    });
    const dependents = modules.filter(
      (other) =>
        other.module_key !== mod.module_key &&
        (other.status === 'installed' || other.status === 'update_available') &&
        (other.dependencies ?? []).includes(mod.module_key),
    );

    return (
      <Grid key={mod.module_key} size={{ xs: 12, sm: 6, md: 4 }}>
        <Card
          variant="outlined"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: 4, transform: 'translateY(-4px)' },
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={1}>
              <Typography variant="h6" fontWeight={600}>
                {mod.display_name}
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                {mod.status === 'installed' && (
                  <Chip label="Installed" size="small" color="success" icon={<CheckCircle />} />
                )}
                {mod.status === 'update_available' && (
                  <Chip label="Update available" size="small" color="warning" />
                )}
              </Box>
            </Box>
            {mod.description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mod.description}
              </Typography>
            ) : null}
            {requires.map((name) => (
              <Typography key={name} variant="body2" color="text.secondary">
                {`Requires ${name}`}
              </Typography>
            ))}
            <Chip label={versionLabel} size="small" variant="outlined" />
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
            {mod.status === 'not_installed' && (
              <Button
                startIcon={<InstallIcon />}
                variant="contained"
                color="primary"
                onClick={() => handleInstall(mod.module_key)}
                disabled={isInstallBusy}
                fullWidth
              >
                {isInstallBusy ? 'Installing...' : 'Install'}
              </Button>
            )}
            {mod.status === 'installed' && (
              <>
                <Button
                  startIcon={<PublishIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={() => setPublishTarget(mod)}
                  sx={{ flex: 1, minWidth: 110 }}
                >
                  Publish
                </Button>
                <Button
                  startIcon={<ManageIcon />}
                  variant="outlined"
                  onClick={() => setAuthoring({ moduleKey: mod.module_key })}
                  sx={{ flex: 1, minWidth: 140 }}
                >
                  Manage objects
                </Button>
                {mod.has_demo && (
                  <Button
                    startIcon={<DemoIcon />}
                    variant="outlined"
                    onClick={() => handleLoadDemo(mod)}
                    disabled={demoMutation.isLoading}
                    sx={{ flex: 1, minWidth: 140 }}
                  >
                    {demoMutation.isLoading ? 'Loading…' : 'Load demo data'}
                  </Button>
                )}
                <Button
                  startIcon={<DeleteIcon />}
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirmUninstall(mod)}
                  disabled={isUninstallBusy || dependents.length > 0}
                  sx={{ flex: 1, minWidth: 110 }}
                >
                  Uninstall
                </Button>
              </>
            )}
            {mod.status === 'update_available' && (
              <>
                <Button
                  startIcon={<UpdateIcon />}
                  variant="contained"
                  color="warning"
                  onClick={() => handleUpdate(mod.module_key)}
                  disabled={isUpdateBusy}
                  sx={{ flex: 1, minWidth: 140 }}
                >
                  {isUpdateBusy
                    ? 'Updating…'
                    : `${mod.installed_version} → ${mod.version}`}
                </Button>
                <Button
                  startIcon={<PublishIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={() => setPublishTarget(mod)}
                  sx={{ flex: 1, minWidth: 110 }}
                >
                  Publish
                </Button>
                <Button
                  startIcon={<ManageIcon />}
                  variant="outlined"
                  onClick={() => setAuthoring({ moduleKey: mod.module_key })}
                  sx={{ flex: 1, minWidth: 140 }}
                >
                  Manage objects
                </Button>
                <Button
                  startIcon={<DeleteIcon />}
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirmUninstall(mod)}
                  disabled={isUninstallBusy || dependents.length > 0}
                  sx={{ flex: 1, minWidth: 110 }}
                >
                  Uninstall
                </Button>
              </>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <ExtensionIcon sx={{ fontSize: 40 }} />
        <Typography variant="h4" component="h1" fontWeight={600}>
          Available Modules
        </Typography>
        <Box flexGrow={1} />
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setAuthoring({ moduleKey: null })}
        >
          New module
        </Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Install modules from the registry to extend your configuration with pre-built objects,
        forms, and seed data.
      </Typography>

      {registryUnreachable && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Module registry is unreachable — showing cached data
        </Alert>
      )}

      {bundles.length > 0 && (
        <>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Bundles
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {bundles.map((bundle) => (
              <Grid key={bundle.bundle_key} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{bundle.display_name}</Typography>
                    {bundle.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {bundle.description}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      {bundle.module_keys.join(' · ')}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      startIcon={<InstallIcon />}
                      disabled={isBundleBusy}
                      onClick={() => handleInstallBundle(bundle)}
                    >
                      Install bundle
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mb: 4 }} />
        </>
      )}

      {localOnly.length > 0 && (
        <>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
            Local modules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Authored on this connection, not yet published to the registry.
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {localOnly.map((mod) => (
              <Grid key={mod.module_key} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                      gap={1}
                    >
                      <Typography variant="h6" fontWeight={600}>
                        {mod.module_key}
                      </Typography>
                      <Chip label="Local" size="small" color="info" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {mod.object_names.length} object
                      {mod.object_names.length === 1 ? '' : 's'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      startIcon={<PublishIcon />}
                      variant="contained"
                      color="primary"
                      onClick={() => setPublishLocalKey(mod.module_key)}
                      sx={{ flex: 1, minWidth: 110 }}
                    >
                      Publish
                    </Button>
                    <Button
                      startIcon={<ManageIcon />}
                      variant="outlined"
                      onClick={() => setAuthoring({ moduleKey: mod.module_key })}
                      sx={{ flex: 1, minWidth: 140 }}
                    >
                      Manage objects
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mb: 4 }} />
        </>
      )}

      {modules.length > 0 ? (
        <>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Modules
          </Typography>
          <Grid container spacing={3}>
            {modules.map((mod) => renderModuleCard(mod))}
          </Grid>
        </>
      ) : (
        !registryUnreachable && (
          <Alert severity="info">No modules published yet</Alert>
        )
      )}

      {modules.length === 0 && registryUnreachable && (
        <Alert severity="info">No cached modules available.</Alert>
      )}

      <Dialog open={!!confirmUninstall} onClose={() => setConfirmUninstall(null)}>
        <DialogTitle>Uninstall &quot;{confirmUninstall?.display_name}&quot;?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the module from this connection, drop its tables, and clean up
            related configuration. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUninstall(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmUninstall && handleUninstall(confirmUninstall)}
            disabled={isUninstallBusy}
          >
            {isUninstallBusy ? 'Uninstalling…' : 'Uninstall'}
          </Button>
        </DialogActions>
      </Dialog>

      {publishTarget ? (
        <PublishModuleDialog
          open={!!publishTarget}
          moduleKey={publishTarget.module_key}
          connectionId={connectionId}
          currentVersion={publishTarget.installed_version ?? publishTarget.version}
          onClose={() => setPublishTarget(null)}
          onPublished={(version, meta) => {
            setPublishTarget(null);
            setToast({
              severity: meta?.stamped === false ? 'warning' : 'success',
              message:
                meta?.stamped === false
                  ? 'Published, but this connection still shows the previous version'
                  : `Published "${publishTarget.display_name}" v${version}.`,
            });
            refetchCatalog();
          }}
        />
      ) : null}

      {publishLocalKey ? (
        <PublishModuleDialog
          open={!!publishLocalKey}
          moduleKey={publishLocalKey}
          connectionId={connectionId}
          currentVersion={null}
          onClose={() => setPublishLocalKey(null)}
          onPublished={(version, meta) => {
            setPublishLocalKey(null);
            handleLocalPublished(publishLocalKey, version, meta);
          }}
        />
      ) : null}

      {authoring ? (
        <NewModuleDialog
          open={!!authoring}
          connectionId={connectionId}
          moduleKey={authoring.moduleKey}
          currentObjects={authoring.moduleKey ? compositionOf(authoring.moduleKey) : []}
          unassigned={unassignedObjects}
          foreignOwners={foreignOwnersFor(authoring.moduleKey)}
          onClose={() => setAuthoring(null)}
          onSaved={() => {
            setAuthoring(null);
            setToast({ severity: 'success', message: 'Module composition saved.' });
            refetchLocal();
          }}
        />
      ) : null}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
};
